import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useAuth } from "./AuthContext";

const ModulesContext = createContext(null);
const STORAGE_KEY = "modules_status";

// Hydrate from the last known snapshot so the sidebar doesn't flash
// soon-to-be-hidden items while the fresh status is in flight.
function readStored() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

export function ModulesProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [modules, setModules] = useState(readStored); // { [key]: enabled } — missing key defaults to enabled
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    if (!isAuthenticated) return Promise.resolve();
    return api
      .get("/api/modules/status")
      .then((res) => {
        const next = res.data?.modules || {};
        setModules(next);
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setModules({});
      setLoaded(false);
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
      return;
    }
    refresh();
  }, [isAuthenticated, refresh]);

  // Live updates: NotificationContext's socket re-dispatches the backend's
  // "modules_changed" broadcast as a window event (this provider sits above
  // it in the tree, so a window event is the clean bridge). Also refetch on
  // tab focus so long-idle sessions converge without a reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onChanged = () => refresh();
    window.addEventListener("hrms:modules_changed", onChanged);
    window.addEventListener("focus", onChanged);
    return () => {
      window.removeEventListener("hrms:modules_changed", onChanged);
      window.removeEventListener("focus", onChanged);
    };
  }, [isAuthenticated, refresh]);

  const isEnabled = useCallback((key) => (key ? modules[key] !== false : true), [modules]);

  const value = useMemo(() => ({ modules, isEnabled, loaded, refresh }), [modules, isEnabled, loaded, refresh]);

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
}

export function useModules() {
  const ctx = useContext(ModulesContext);
  if (!ctx) throw new Error("useModules must be used inside <ModulesProvider>");
  return ctx;
}
